import { Button, CircleQuestionIcon, Label, Paragraph } from '@quickmd-llc/design-system'
import { useAutoUpdatingRef } from '@quickmd-llc/frontend-utils'
import { PROVIDER_EVENT_NAMES, tracking } from 'lib/segment/tracking'
import { homeDeliveryGuideHref } from 'pages/HomeDeliveryGuide/homeDeliveryGuideLink'
import {
  HOME_DELIVERY_NUDGE_BUTTON_LABEL,
  HOME_DELIVERY_NUDGE_TYPE,
} from 'pages/HomeDeliveryGuide/homeDeliveryTracking'
import { useEffect, useId, useLayoutEffect, useRef } from 'react'
import { useSelector } from 'store/hooks/useSelector'
import { selectProviderId } from 'store/waitingRoom/WaitingRoomSelectors'
import { homeDeliveryGuidance, type RequestedPharmacy, sentToPickup } from '../requestedPharmacy'
import { HomeDeliveryGuidanceStrip } from './HomeDeliveryGuidanceStrip'
import { useHdNudgeDismissal } from './hdNudgeDismissals'
import { type VisitPrescription } from './prescriptionFulfilment'

type Props = {
  visitId: string | null | undefined
  patientId: string | null | undefined
  requested: RequestedPharmacy | null
  prescriptions: readonly VisitPrescription[] | null | undefined
}

// Pickup rows always carry a `PrescriptionId`; the fallback only keeps a malformed row from sharing
// a dismissal with another.
const prescriptionKey = (p: VisitPrescription): string => p.PrescriptionId ?? `${p.DrugName ?? ''}@${p.CreatedAt ?? ''}`

/**
 * QMD-4913: the pharmacy the patient asked for, at the top of the Orders card, with a strip that
 * nudges toward Order Set on Home Delivery visits and turns to a warning once a sent script went to
 * a pickup pharmacy instead.
 *
 * `prescriptions` is the visit's prescriptions query, which the `prescriptionchanged` MQTT handler
 * rewrites (`getVisitPrescriptionCollection`), so the strip re-evaluates live as ScriptSure calls
 * back as well as on load.
 *
 * Hidden when the patient requested nothing, or this visit method shows no requested pharmacy.
 *
 * The `?` and the strip's buttons link to the Home Delivery guide page in a new tab.
 */
export const RequestedPharmacyCard = ({ visitId, patientId, requested, prescriptions }: Props) => {
  const guidance = homeDeliveryGuidance(requested, prescriptions)
  const providerId = useSelector(selectProviderId)
  const [isDismissed, dismiss] = useHdNudgeDismissal(
    providerId,
    visitId,
    guidance,
    sentToPickup(prescriptions).map(prescriptionKey),
  )
  const strip = guidance && !isDismissed ? guidance : null
  const helpRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null)
  const focusInStrip = useRef(false)
  const newTabNoteId = useId()

  // QMD-4967. What every nudge event carries, or `null` while there is no strip, the visit and provider
  // profile haven't loaded, or the prescriptions haven't. Before they load every Home Delivery visit
  // reads as blue, even one whose pickup script is about to turn it yellow, so none of the three
  // events counts a nudge until then, and an Acknowledged or How Clicked never lacks its Shown.
  const nudgeProperties =
    strip && providerId && visitId && patientId && prescriptions !== undefined ?
      { NudgeType: HOME_DELIVERY_NUDGE_TYPE[strip], PatientId: patientId, ProviderId: providerId, VisitId: visitId }
    : null
  const nudgePropertiesRef = useAutoUpdatingRef(nudgeProperties)

  // One impression per nudge per mount. The popup re-renders on every Daily call event, and the strip
  // can go blue → yellow → blue as a pickup script is sent and then canceled, so every nudge reported
  // is remembered. "Got it" forgets it: a warning that comes back for a later pickup script is new.
  const shownNudge = nudgeProperties ? `${nudgeProperties.VisitId}:${nudgeProperties.NudgeType}` : null
  const shownNudgesRef = useRef(new Set<string>())
  useEffect(() => {
    const properties = nudgePropertiesRef.current
    if (!shownNudge || !properties || shownNudgesRef.current.has(shownNudge)) return
    shownNudgesRef.current.add(shownNudge)
    tracking.trackEvent(PROVIDER_EVENT_NAMES.HOME_DELIVERY_NUDGE_SHOWN, properties)
  }, [shownNudge, nudgePropertiesRef])

  const acknowledge = () => {
    if (nudgeProperties) tracking.trackEvent(PROVIDER_EVENT_NAMES.HOME_DELIVERY_NUDGE_ACKNOWLEDGED, nudgeProperties)
    if (shownNudge) shownNudgesRef.current.delete(shownNudge)
    dismiss()
  }

  const trackHowClicked = () => {
    if (!strip || !nudgeProperties) return
    tracking.trackEvent(PROVIDER_EVENT_NAMES.HOME_DELIVERY_NUDGE_HOW_CLICKED, {
      ...nudgeProperties,
      ButtonLabel: HOME_DELIVERY_NUDGE_BUTTON_LABEL[strip],
    })
  }

  // The strip's buttons go away under focus on "Got it", and also when a refetch or the MQTT handler
  // clears the warning. Hand focus to the card's own help link rather than letting it fall to <body>,
  // where the popup's focus trap would restart from the top. A layout effect, so it lands before paint.
  useLayoutEffect(() => {
    if (strip || !focusInStrip.current) return
    focusInStrip.current = false
    // Focus that has since moved somewhere real (a click elsewhere blurs with no `relatedTarget` too)
    // stays where it is.
    if (document.activeElement && document.activeElement !== document.body) return
    helpRef.current?.focus()
  }, [strip])

  if (!requested || requested.kind === 'none') return null

  return (
    <div className='flex flex-col overflow-clip rounded-lg border border-border-medium bg-white'>
      <div className='flex flex-col gap-sp-1 p-sp-2'>
        <Label size='sm' className='text-text-light'>
          Requested pharmacy
        </Label>
        <div className='flex items-start justify-between gap-sp-1'>
          <Paragraph size='sm' className='min-w-0 text-text-dark'>
            {requested.kind === 'homeDelivery' ?
              <strong className='font-semibold'>Home Delivery</strong>
            : <>
                <strong className='font-semibold'>Pharmacy pickup</strong>
                {requested.pharmacy.Name ? ` at ${requested.pharmacy.Name}` : null}
              </>
            }
          </Paragraph>
          {requested.kind === 'homeDelivery' ?
            <Button
              ref={helpRef}
              size='s'
              priority='tertiary'
              isIcon
              aria-label='How Home Delivery works'
              aria-describedby={newTabNoteId}
              className='shrink-0'
              to={homeDeliveryGuideHref({ from: 'help_icon' })}
              target='_blank'
              rel='noopener noreferrer'>
              <CircleQuestionIcon iconSize='m' />
            </Button>
          : null}
          <span id={newTabNoteId} hidden>
            Opens in a new tab
          </span>
        </div>
      </div>
      {/* Focus counts as still inside only when it leaves with the element itself, which is then
          detached. A click on blank space also blurs with no `relatedTarget`, but leaves the element
          where it was, so that one counts as leaving. */}
      <div
        onFocus={() => {
          focusInStrip.current = true
        }}
        onBlur={(event) => {
          if (event.relatedTarget || event.target.isConnected) focusInStrip.current = false
        }}>
        <HomeDeliveryGuidanceStrip tone={strip} onDismiss={acknowledge} onHowClick={trackHowClicked} />
      </div>
    </div>
  )
}
